import React, { useState } from 'react';
import { CheckCircle, XCircle, Loader2, Building2, FileText, ToggleLeft, ToggleRight } from 'lucide-react';
import { CompanyInfo, VerificationResult, PeppolResult, VerificationType, DualVerificationResult } from '../types';
import { VatVerificationService } from '../services/vatVerificationService';
import { PeppolVerificationService } from '../services/peppolVerificationService';
import { DualVerificationService } from '../services/dualVerificationService';
import { useLocalization } from '../contexts/LocalizationContext';
import { Analytics } from '../utils/analytics';

const SingleVerification: React.FC = () => {
  const { t, language } = useLocalization();
  const [verificationType, setVerificationType] = useState<VerificationType>('vat');
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState<VerificationResult | PeppolResult | DualVerificationResult | null>(null);
  const [useRealAPI, setUseRealAPI] = useState(true);
  
  // Unified form state
  const [identifier, setIdentifier] = useState('');
  
  // VAT form state (for VAT-only verification)
  const [vatNumber, setVatNumber] = useState('');
  
  // Peppol form state (for Peppol-only verification)
  const [peppolId, setPeppolId] = useState('');


  const handleVerificationTypeChange = (newType: VerificationType) => {
    Analytics.singleVerificationInput(newType === 'both' ? 'dual' : newType);
    setVerificationType(newType);
    setResult(null); // Clear previous validation results
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    setResult(null);

    // Set the API mode for all services
    DualVerificationService.setUseRealAPI(useRealAPI);

    try {
      if (verificationType === 'vat') {
        Analytics.singleVerificationSubmit('vat', vatNumber);
        const vatForm: CompanyInfo = {
          name: '',
          vatNumber: vatNumber,
          address: '',
          country: '',
          city: '',
          postalCode: ''
        };
        const vatResult = await VatVerificationService.verifyVatNumber(vatForm, language);
        setResult(vatResult);
      } else if (verificationType === 'peppol') {
        Analytics.singleVerificationSubmit('peppol', peppolId);
        const peppolResult = await PeppolVerificationService.verifyPeppolId(peppolId, language);
        setResult(peppolResult);
      } else if (verificationType === 'both') {
        Analytics.singleVerificationSubmit('dual', identifier);
        const dualResult = await DualVerificationService.verifyBoth(identifier, '', language);
        setResult(dualResult);
      }
    } catch (error) {
      console.error('Verification error:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const resetForm = () => {
    setIdentifier('');
    setVatNumber('');
    setPeppolId('');
    setResult(null);
  };

  const renderVatForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t.singleVerification.forms.vatNumber} *
        </label>
        <input
          type="text"
          value={vatNumber}
          onChange={(e) => setVatNumber(e.target.value)}
          className="input-field"
          placeholder={t.singleVerification.forms.vatNumberPlaceholder}
          required
        />
      </div>
    </div>
  );

  const renderPeppolForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t.singleVerification.forms.peppolId} *
        </label>
        <input
          type="text"
          value={peppolId}
          onChange={(e) => setPeppolId(e.target.value)}
          className="input-field"
          placeholder={t.singleVerification.forms.peppolIdPlaceholder}
          required
        />
        <p className="text-sm text-gray-500 mt-1">
          {t.singleVerification.forms.peppolIdHelp}
        </p>
      </div>
    </div>
  );

  const renderDualForm = () => (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg border border-blue-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.singleVerification.forms.unifiedTitle}</h3>
        <p className="text-sm text-gray-600 mb-4">
          {t.singleVerification.forms.unifiedDescription}
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.singleVerification.forms.companyIdentifier} *
            </label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="input-field"
              placeholder={t.singleVerification.forms.companyIdentifierPlaceholder}
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              {t.singleVerification.forms.companyIdentifierHelp}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderResult = () => {
    if (!result) return null;

    // Handle dual verification result
    if ('vatResult' in result && 'peppolResult' in result) {
      return renderDualResult(result as DualVerificationResult);
    }

    const isVatResult = 'vatNumber' in result;
    const isValid = isVatResult ? result.isValid : (result as PeppolResult).isValid;
    const statusClass = isValid ? 'status-valid' : 'status-invalid';
    const StatusIcon = isValid ? CheckCircle : XCircle;

    return (
      <div className={`card ${statusClass} mt-6`}>
        <div className="flex items-center gap-3 mb-4">
          <StatusIcon className="w-6 h-6" />
          <h3 className="text-lg font-semibold">
            {isValid ? t.singleVerification.results.verificationSuccessful : t.singleVerification.results.verificationFailed}
          </h3>
        </div>
        
        <div className="space-y-2">
          {isVatResult ? (
            <div className="flex justify-between">
              <span className="font-medium">{t.singleVerification.results.fields.vatNumber}:</span>
              <span>{result.vatNumber}</span>
            </div>
          ) : (
            <>
              {(result as PeppolResult).fullParticipantId && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.fullParticipantId}:</span>
                  <span className="text-sm font-mono">{(result as PeppolResult).fullParticipantId}</span>
                </div>
              )}
              
              {(result as PeppolResult).participantName && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.participantName}:</span>
                  <span>{String((result as PeppolResult).participantName)}</span>
                </div>
              )}
              
              {(result as PeppolResult).participantId && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.participantId}:</span>
                  <span>{(result as PeppolResult).participantId}</span>
                </div>
              )}
              
              {(result as PeppolResult).scheme && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.scheme}:</span>
                  <span>{(result as PeppolResult).scheme}</span>
                </div>
              )}
              
              {(result as PeppolResult).allSupportedDocumentTypes && (!(result as PeppolResult).matchCount || (result as PeppolResult).matchCount! <= 1) && (
                <div className="space-y-2">
                  <span className="font-medium">{t.singleVerification.results.fields.supportedDocumentTypes}:</span>
                  <div className="text-xs text-gray-600 max-w-full break-all whitespace-pre-line">
                    {(result as PeppolResult).allSupportedDocumentTypes!.split(',').map((docType, index) => (
                      <div key={index} className="mb-1">{docType.trim()}</div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="font-medium">{t.singleVerification.results.fields.supportsBilling}:</span>
                <span className={`flex items-center gap-1 ${(result as PeppolResult).supportsBilling ? 'text-green-600' : 'text-red-600'}`}>
                  {(result as PeppolResult).supportsBilling ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      {t.singleVerification.results.fields.yes}
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      {t.singleVerification.results.fields.no}
                    </>
                  )}
                </span>
              </div>
              
              {(result as PeppolResult).matchCount && (result as PeppolResult).matchCount! > 1 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-blue-800">{t.singleVerification.results.multipleMatches.title}:</span>
                    <span className="text-sm text-blue-600">{(result as PeppolResult).matchCount} {t.singleVerification.results.multipleMatches.matchCount}</span>
                  </div>
                  <div className="space-y-3">
                    {(result as PeppolResult).allParticipantIds && (result as PeppolResult).allEntityNames && (result as PeppolResult).allSupportedDocumentTypes && (
                      <div className="text-sm space-y-3">
                        {(() => {
                          const participantIds = (result as PeppolResult).allParticipantIds!.split('|');
                          const entityNames = (result as PeppolResult).allEntityNames!.split('|');
                          const docTypes = (result as PeppolResult).allSupportedDocumentTypes!.split('|');
                          
                          return participantIds.map((id, index) => {
                            const entityName = entityNames[index] || 'Unknown';
                            const matchDocTypes = docTypes[index] || '';
                            const hasBilling = matchDocTypes.includes('urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1') && 
                                              matchDocTypes.includes('urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1');
                            
                            return (
                              <div key={index} className="bg-white p-3 rounded border border-blue-100">
                                <div className="font-medium text-blue-700 mb-2">{t.singleVerification.results.multipleMatches.match} {index + 1}:</div>
                                <div className="space-y-1 text-xs">
                                  <div><span className="font-medium">ID:</span> <span className="font-mono text-blue-600">{id.trim()}</span></div>
                                  <div><span className="font-medium">Name:</span> <span className="text-blue-600">{entityName.trim()}</span></div>
                                  <div><span className="font-medium">{t.singleVerification.results.fields.supportsBilling}:</span> 
                                    <span className={`ml-1 ${hasBilling ? 'text-green-600' : 'text-red-600'}`}>
                                      {hasBilling ? t.singleVerification.results.fields.yes : t.singleVerification.results.fields.no}
                                    </span>
                                  </div>
                                  <div><span className="font-medium">Document Types:</span></div>
                                  <div className="ml-2 text-xs text-gray-600 max-h-20 overflow-y-auto">
                                    {matchDocTypes.split(',').map((docType, docIndex) => (
                                      <div key={docIndex} className="break-all">{docType.trim()}</div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          
          {isValid && (
            <>
              {isVatResult && result.companyName && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.companyName}:</span>
                  <span>{result.companyName}</span>
                </div>
              )}
              
              {isVatResult && result.address && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.address}:</span>
                  <span>{result.address}</span>
                </div>
              )}
            </>
          )}
          
          {(isVatResult ? result.error : (result as PeppolResult).error) && (
            <div className="flex justify-between">
              <span className="font-medium">{t.singleVerification.results.fields.error}:</span>
              <span className="text-red-600">{isVatResult ? result.error : (result as PeppolResult).error}</span>
            </div>
          )}
          
          <div className="flex justify-between text-sm text-gray-500">
            <span>{t.singleVerification.results.fields.verifiedAt}:</span>
            <span>{new Date(result.timestamp).toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderDualResult = (dualResult: DualVerificationResult) => {
    const { vatResult, peppolResult } = dualResult;
    
    return (
      <div className="mt-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">{t.singleVerification.results.verificationResults}</h3>
        
        {vatResult && (
          <div className={`card ${vatResult.isValid ? 'status-valid' : 'status-invalid'}`}>
            <div className="flex items-center gap-3 mb-4">
              {vatResult.isValid ? <CheckCircle className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
              <h4 className="text-lg font-semibold">{t.singleVerification.results.vatVerification}</h4>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">{t.singleVerification.results.fields.vatNumber}:</span>
                <span>{vatResult.vatNumber}</span>
              </div>
              
              {vatResult.isValid && vatResult.companyName && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.companyName}:</span>
                  <span>{vatResult.companyName}</span>
                </div>
              )}
              
              {vatResult.isValid && vatResult.address && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.address}:</span>
                  <span>{vatResult.address}</span>
                </div>
              )}
              
              {vatResult.error && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.error}:</span>
                  <span className="text-red-600">{vatResult.error}</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {peppolResult && (
          <div className={`card ${peppolResult.isValid ? 'status-valid' : 'status-invalid'}`}>
            <div className="flex items-center gap-3 mb-4">
              {peppolResult.isValid ? <CheckCircle className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
              <h4 className="text-lg font-semibold">{t.singleVerification.results.peppolVerification}</h4>
            </div>
            
            <div className="space-y-2">
              {peppolResult.fullParticipantId && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.fullParticipantId}:</span>
                  <span className="text-sm font-mono">{peppolResult.fullParticipantId}</span>
                </div>
              )}
              
              {peppolResult.participantName && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.participantName}:</span>
                  <span>{String(peppolResult.participantName)}</span>
                </div>
              )}
              
              {peppolResult.participantId && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.participantId}:</span>
                  <span>{peppolResult.participantId}</span>
                </div>
              )}
              
              {peppolResult.scheme && (
                <div className="flex justify-between">
                  <span className="font-medium">{t.singleVerification.results.fields.scheme}:</span>
                  <span>{peppolResult.scheme}</span>
                </div>
              )}
              
              {peppolResult.allSupportedDocumentTypes && (!peppolResult.matchCount || peppolResult.matchCount <= 1) && (
                <div className="space-y-2">
                  <span className="font-medium">{t.singleVerification.results.fields.supportedDocumentTypes}:</span>
                  <div className="text-xs text-gray-600 max-w-full break-all whitespace-pre-line">
                    {peppolResult.allSupportedDocumentTypes.split(',').map((docType, index) => (
                      <div key={index} className="mb-1">{docType.trim()}</div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="font-medium">{t.singleVerification.results.fields.supportsBilling}:</span>
                <span className={`flex items-center gap-1 ${peppolResult.supportsBilling ? 'text-green-600' : 'text-red-600'}`}>
                  {peppolResult.supportsBilling ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      {t.singleVerification.results.fields.yes}
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      {t.singleVerification.results.fields.no}
                    </>
                  )}
                </span>
              </div>
              
              {peppolResult.matchCount && peppolResult.matchCount > 1 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-blue-800">{t.singleVerification.results.multipleMatches.title}:</span>
                    <span className="text-sm text-blue-600">{peppolResult.matchCount} {t.singleVerification.results.multipleMatches.matchCount}</span>
                  </div>
                  <div className="space-y-3">
                    {peppolResult.allParticipantIds && peppolResult.allEntityNames && peppolResult.allSupportedDocumentTypes && (
                      <div className="text-sm space-y-3">
                        {(() => {
                          const participantIds = peppolResult.allParticipantIds.split('|');
                          const entityNames = peppolResult.allEntityNames.split('|');
                          const docTypes = peppolResult.allSupportedDocumentTypes.split('|');
                          
                          return participantIds.map((id, index) => {
                            const entityName = entityNames[index] || 'Unknown';
                            const matchDocTypes = docTypes[index] || '';
                            const hasBilling = matchDocTypes.includes('urn:oasis:names:specification:ubl:schema:xsd:Invoice-2::Invoice##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1') && 
                                              matchDocTypes.includes('urn:oasis:names:specification:ubl:schema:xsd:CreditNote-2::CreditNote##urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0::2.1');
                            
                            return (
                              <div key={index} className="bg-white p-3 rounded border border-blue-100">
                                <div className="font-medium text-blue-700 mb-2">{t.singleVerification.results.multipleMatches.match} {index + 1}:</div>
                                <div className="space-y-1 text-xs">
                                  <div><span className="font-medium">ID:</span> <span className="font-mono text-blue-600">{id.trim()}</span></div>
                                  <div><span className="font-medium">Name:</span> <span className="text-blue-600">{entityName.trim()}</span></div>
                                  <div><span className="font-medium">{t.singleVerification.results.fields.supportsBilling}:</span> 
                                    <span className={`ml-1 ${hasBilling ? 'text-green-600' : 'text-red-600'}`}>
                                      {hasBilling ? t.singleVerification.results.fields.yes : t.singleVerification.results.fields.no}
                                    </span>
                                  </div>
                                  <div><span className="font-medium">Document Types:</span></div>
                                  <div className="ml-2 text-xs text-gray-600 max-h-20 overflow-y-auto">
                                    {matchDocTypes.split(',').map((docType, docIndex) => (
                                      <div key={docIndex} className="break-all">{docType.trim()}</div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {peppolResult.error && (
                <div className="flex justify-between">
                  <span className="font-medium">Error:</span>
                  <span className="text-red-600">{peppolResult.error}</span>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="text-sm text-gray-500 text-center">
          Verified at: {new Date(dualResult.timestamp).toLocaleString()}
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-primary-600" />
          <h2 className="text-2xl font-bold text-gray-900">{t.singleVerification.title}</h2>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{t.singleVerification.apiMode.simulationMode}</span>
          <button
            onClick={() => setUseRealAPI(!useRealAPI)}
            className="flex items-center gap-2 text-sm font-medium"
          >
            {useRealAPI ? (
              <>
                <ToggleRight className="w-5 h-5 text-primary-600" />
                <span className="text-primary-600">{t.singleVerification.apiMode.realAPI}</span>
              </>
            ) : (
              <>
                <ToggleLeft className="w-5 h-5 text-gray-400" />
                <span className="text-gray-500">{t.singleVerification.apiMode.simulation}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          {t.singleVerification.verificationType}
        </label>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              value="vat"
              checked={verificationType === 'vat'}
              onChange={(e) => handleVerificationTypeChange(e.target.value as VerificationType)}
              className="mr-2"
            />
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t.singleVerification.types.vat}
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="peppol"
              checked={verificationType === 'peppol'}
              onChange={(e) => handleVerificationTypeChange(e.target.value as VerificationType)}
              className="mr-2"
            />
            <span className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {t.singleVerification.types.peppol}
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="both"
              checked={verificationType === 'both'}
              onChange={(e) => handleVerificationTypeChange(e.target.value as VerificationType)}
              className="mr-2"
            />
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {t.singleVerification.types.both}
            </span>
          </label>
        </div>
      </div>

      {verificationType === 'vat' ? renderVatForm() : 
       verificationType === 'peppol' ? renderPeppolForm() : 
       renderDualForm()}

      <div className="flex gap-3 mt-6">
        <button
          onClick={handleVerify}
          disabled={isVerifying || 
            (verificationType === 'vat' ? !vatNumber : 
             verificationType === 'peppol' ? !peppolId : 
             !identifier)}
          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isVerifying ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t.singleVerification.buttons.verifying}
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              {t.singleVerification.buttons.verify}
            </>
          )}
        </button>
        
        <button
          onClick={resetForm}
          className="btn-secondary"
        >
          {t.singleVerification.buttons.reset}
        </button>
      </div>

      {renderResult()}
    </div>
  );
};

export default SingleVerification;
